import { useCallback, useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { SharedSessionResponse, SharedSessionParticipant } from '../services/types/api.generated';
import { WebSocketConnectionState } from './useWebSocketConnection';

const DEFAULT_PING_INTERVAL_MS = 4000;
const NORMAL_CLOSE_CODE = 1000;
const SESSION_ENDED_CODE = 4400;
const STOPPED_CODE = 4001;
const MAX_RECONNECT_DELAY_MS = 30000;
const DISCONNECT_RESET_DELAY_MS = 100;

interface CrewWebSocketsState {
  telemetry: WebSocketConnectionState;
  ping: WebSocketConnectionState;
}

interface PingPongMessage {
  message: 'pong';
  data: {
    session: SharedSessionResponse;
    participants: SharedSessionParticipant[];
  };
}

interface PingEventMessage {
  op: 'event';
  event: string;
  extra: Record<string, unknown>;
}

interface UseCrewWebSocketsOptions {
  telemetryUrlBuilder: (participantId: string) => string;
  pingUrlBuilder: (participantId: string) => string;
  onTelemetryMessage: (data: unknown) => void;
  onPongMessage?: (data: PingPongMessage['data']) => void;
  onEventMessage?: (event: string, extra: Record<string, unknown>) => void;
  onError?: (error: string | null) => void;
  onSessionEnded?: () => void; // Called when session ends (close code 4400)
  maxReconnectAttempts?: number;
  pingInterval?: number; // milliseconds between pings, default 30000 (30 seconds)
  sessionId?: string;
  deviceUuid?: string;
}

interface UseCrewWebSocketsReturn {
  connect: (participantId: string) => void;
  disconnect: () => void;
  connectionState: CrewWebSocketsState;
  isFullyConnected: boolean;
  reconnectAttempts: { telemetry: number; ping: number };
}

export const useCrewWebSockets = (options: UseCrewWebSocketsOptions): UseCrewWebSocketsReturn => {
  const {
    telemetryUrlBuilder,
    pingUrlBuilder,
    onTelemetryMessage,
    onPongMessage,
    onEventMessage,
    onError,
    onSessionEnded,
    maxReconnectAttempts = 5,
    pingInterval = DEFAULT_PING_INTERVAL_MS,
    sessionId,
    deviceUuid
  } = options;

  const { t } = useTranslation();
  const [connectionState, setConnectionState] = useState<CrewWebSocketsState>({
    telemetry: 'disconnected',
    ping: 'disconnected'
  });

  const isDisconnectingRef = useRef<boolean>(false);

  const telemetryRefs = useRef({
    ws: null as WebSocket | null,
    reconnectTimeout: null as NodeJS.Timeout | null,
    reconnectAttempts: 0,
    participantId: null as string | null
  });

  const pingRefs = useRef({
    ws: null as WebSocket | null,
    reconnectTimeout: null as NodeJS.Timeout | null,
    reconnectAttempts: 0,
    interval: null as NodeJS.Timeout | null,
    participantId: null as string | null
  });

  const callbacksRef = useRef({
    onTelemetryMessage,
    onPongMessage,
    onEventMessage,
    onError,
    onSessionEnded,
    telemetryUrlBuilder,
    pingUrlBuilder
  });

  useEffect(() => {
    callbacksRef.current = {
      onTelemetryMessage,
      onPongMessage,
      onEventMessage,
      onError,
      onSessionEnded,
      telemetryUrlBuilder,
      pingUrlBuilder
    };
  });

  const isSessionEnded = (code: number, reason?: string) =>
    code === SESSION_ENDED_CODE || (code === STOPPED_CODE && reason === 'stopped');

  const calculateReconnectDelay = (attempts: number) => Math.min(1000 * 2 ** attempts, MAX_RECONNECT_DELAY_MS);

  type ConnectionRefs = typeof telemetryRefs | typeof pingRefs;

  const cleanupConnection = (refs: ConnectionRefs) => {
    if (refs.current.reconnectTimeout) {
      clearTimeout(refs.current.reconnectTimeout);
      // eslint-disable-next-line no-param-reassign
      refs.current.reconnectTimeout = null;
    }
    if (refs.current.ws) {
      refs.current.ws.close();
      // eslint-disable-next-line no-param-reassign
      refs.current.ws = null;
    }
  };

  const updateConnectionState = (type: 'telemetry' | 'ping', state: WebSocketConnectionState) => {
    setConnectionState((prev) => ({ ...prev, [type]: state }));
  };

  const handleReconnect = (
    refs: ConnectionRefs,
    type: 'telemetry' | 'ping',
    url: string,
    reconnectFn: (url: string) => void,
    errorMessage?: string
  ) => {
    const delay = calculateReconnectDelay(refs.current.reconnectAttempts);
    updateConnectionState(type, 'error');
    // eslint-disable-next-line no-param-reassign
    refs.current.reconnectAttempts++;
    // eslint-disable-next-line no-param-reassign
    refs.current.reconnectTimeout = setTimeout(() => {
      if (!isDisconnectingRef.current) reconnectFn(url);
    }, delay);
  };

  const createWebSocketConnection = (
    url: string,
    type: 'telemetry' | 'ping',
    refs: ConnectionRefs,
    onMessage: (event: MessageEvent) => void,
    onReconnect: (url: string) => void,
    errorMessage?: string
  ) => {
    if (!url || url.trim() === '') {
      updateConnectionState(type, 'disconnected');
      return;
    }

    cleanupConnection(refs);

    if ('interval' in refs.current && refs.current.interval) {
      clearInterval(refs.current.interval);
      // eslint-disable-next-line no-param-reassign
      refs.current.interval = null;
    }

    updateConnectionState(type, 'connecting');

    const ws = new WebSocket(url);

    ws.onopen = () => {
      // eslint-disable-next-line no-param-reassign
      refs.current.reconnectAttempts = 0;
      updateConnectionState(type, 'connected');
      callbacksRef.current.onError?.(null);

      if (type === 'ping' && 'interval' in refs.current) {
        // eslint-disable-next-line no-param-reassign
        refs.current.interval = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, pingInterval);
      }
    };

    ws.onmessage = onMessage;

    ws.onclose = (event) => {
      if (isDisconnectingRef.current) return;

      updateConnectionState(type, 'disconnected');

      if ('interval' in refs.current && refs.current.interval) {
        clearInterval(refs.current.interval);
        // eslint-disable-next-line no-param-reassign
        refs.current.interval = null;
      }

      const isLimitReached = refs.current.reconnectAttempts >= maxReconnectAttempts;
      if (isSessionEnded(event.code, event.reason) || isLimitReached) {
        callbacksRef.current.onSessionEnded?.();
        return;
      }

      const shouldRetry = event.code !== NORMAL_CLOSE_CODE;

      if (shouldRetry && !isLimitReached) {
        handleReconnect(refs, type, url, onReconnect, errorMessage);
      } else if (!isLimitReached) {
        updateConnectionState(type, 'error');
        if (errorMessage) {
          callbacksRef.current.onError?.(errorMessage);
        } else {
          console.error(`${type} WebSocket connection lost after max reconnect attempts`);
        }
      }
    };

    ws.onerror = () => {
      console.error(`Crew ${type} WebSocket error:`, { url, sessionId, deviceUuid });
    };

    // eslint-disable-next-line no-param-reassign
    refs.current.ws = ws;
  };

  const connectTelemetry = useCallback(
    (url: string, participantId?: string) => {
      // Store participantId for reconnection
      if (participantId) {
        telemetryRefs.current.participantId = participantId;
      }

      // Rebuild URL with stored participantId if available during reconnection
      const finalUrl = participantId
        ? callbacksRef.current.telemetryUrlBuilder(participantId)
        : telemetryRefs.current.participantId
        ? callbacksRef.current.telemetryUrlBuilder(telemetryRefs.current.participantId)
        : url;

      // Validate URL contains participant segment (unless MQTT is used and URL is empty)
      if (finalUrl && !finalUrl.includes('/participant/')) {
        console.error('Invalid telemetry URL: missing participant ID', { url: finalUrl, participantId });
        updateConnectionState('telemetry', 'error');
        return;
      }

      createWebSocketConnection(
        finalUrl,
        'telemetry',
        telemetryRefs,
        (event) => {
          try {
            callbacksRef.current.onTelemetryMessage(JSON.parse(event.data));
          } catch (error) {
            console.error('Error parsing telemetry message:', error);
          }
        },
        (reconnectUrl: string) => {
          // Rebuild URL with stored participantId during reconnection
          const reconnectParticipantId = telemetryRefs.current.participantId;
          if (reconnectParticipantId) {
            connectTelemetry(callbacksRef.current.telemetryUrlBuilder(reconnectParticipantId), reconnectParticipantId);
          } else if (finalUrl) {
            // Only reconnect if we have a valid URL (not empty MQTT case)
            console.error('Cannot reconnect telemetry: participantId not available');
            updateConnectionState('telemetry', 'error');
          }
        },
        t('crew_view.errors.telemetry_connection_lost')
      );
    },
    [maxReconnectAttempts, sessionId, deviceUuid, t]
  );

  const connectPing = useCallback(
    (url: string, participantId?: string) => {
      // Store participantId for reconnection
      if (participantId) {
        pingRefs.current.participantId = participantId;
      }

      // Rebuild URL with stored participantId if available during reconnection
      const finalUrl = participantId
        ? callbacksRef.current.pingUrlBuilder(participantId)
        : pingRefs.current.participantId
        ? callbacksRef.current.pingUrlBuilder(pingRefs.current.participantId)
        : url;

      // Validate URL contains participant segment (unless URL is empty for MQTT)
      if (finalUrl && !finalUrl.includes('/participant/')) {
        console.error('Invalid ping URL: missing participant ID', { url: finalUrl, participantId });
        updateConnectionState('ping', 'error');
        return;
      }

      createWebSocketConnection(
        finalUrl,
        'ping',
        pingRefs,
        (event) => {
          try {
            const data = JSON.parse(event.data);
            if (isSessionEnded(data.code, data.reason)) {
              callbacksRef.current.onSessionEnded?.();
              return;
            }
            if (data.op === 'event' && data.event) {
              callbacksRef.current.onEventMessage?.(data.event, data.extra ?? {});
              return;
            }
            if (data.message?.toLowerCase().trim() === 'pong' && data.data) {
              callbacksRef.current.onPongMessage?.(data.data);
            }
          } catch (error) {
            const pongMessage = event.data?.toLowerCase()?.trim();
            if (pongMessage === 'pong') {
              console.warn('Received plain text pong response, expected JSON with session and participants data');
            } else {
              console.error('Error parsing ping pong message:', error);
            }
          }
        },
        (reconnectUrl: string) => {
          // Rebuild URL with stored participantId during reconnection
          const reconnectParticipantId = pingRefs.current.participantId;
          if (reconnectParticipantId) {
            connectPing(callbacksRef.current.pingUrlBuilder(reconnectParticipantId), reconnectParticipantId);
          } else {
            console.error('Cannot reconnect ping: participantId not available');
            updateConnectionState('ping', 'error');
          }
        }
      );
    },
    [maxReconnectAttempts, pingInterval, sessionId, deviceUuid]
  );

  const connect = useCallback(
    (participantId: string) => {
      if (!sessionId || !deviceUuid) {
        console.warn(t('crew_view.warnings.missing_session_id'));
        return;
      }
      if (!participantId || participantId.trim() === '') {
        console.error('Cannot connect: participantId is required');
        return;
      }
      connectTelemetry(callbacksRef.current.telemetryUrlBuilder(participantId), participantId);
      connectPing(callbacksRef.current.pingUrlBuilder(participantId), participantId);
    },
    [sessionId, deviceUuid, connectTelemetry, connectPing, t]
  );

  const cleanupRefs = (refs: ConnectionRefs, hasPendingRetry: boolean) => {
    if (!hasPendingRetry) {
      if (refs.current.reconnectTimeout) {
        clearTimeout(refs.current.reconnectTimeout);
        // eslint-disable-next-line no-param-reassign
        refs.current.reconnectTimeout = null;
      }
      // eslint-disable-next-line no-param-reassign
      refs.current.reconnectAttempts = 0;
    }
    if ('interval' in refs.current && refs.current.interval) {
      clearInterval(refs.current.interval);
      // eslint-disable-next-line no-param-reassign
      refs.current.interval = null;
    }
    if (refs.current.ws) {
      refs.current.ws.close();
      // eslint-disable-next-line no-param-reassign
      refs.current.ws = null;
    }
  };

  const disconnect = useCallback(() => {
    isDisconnectingRef.current = true;

    const hasPendingTelemetryRetry =
      telemetryRefs.current.reconnectTimeout !== null && telemetryRefs.current.reconnectAttempts < maxReconnectAttempts;
    const hasPendingPingRetry =
      pingRefs.current.reconnectTimeout !== null && pingRefs.current.reconnectAttempts < maxReconnectAttempts;

    cleanupRefs(telemetryRefs, hasPendingTelemetryRetry);
    cleanupRefs(pingRefs, hasPendingPingRetry);

    setTimeout(() => {
      isDisconnectingRef.current = false;
    }, DISCONNECT_RESET_DELAY_MS);

    if (!hasPendingTelemetryRetry && !hasPendingPingRetry) {
      setConnectionState({ telemetry: 'disconnected', ping: 'disconnected' });
    }
  }, [maxReconnectAttempts]);

  // Cleanup WebSocket connections on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connect,
    disconnect,
    connectionState,
    isFullyConnected: connectionState.telemetry === 'connected' && connectionState.ping === 'connected',
    reconnectAttempts: {
      telemetry: telemetryRefs.current.reconnectAttempts,
      ping: pingRefs.current.reconnectAttempts
    }
  };
};
